import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Coins, ShieldOff, Home } from "lucide-react";
import { api } from "../api/client";
import { usePlayer } from "../context/PlayerContext";
import { Sound } from "../game/audio";

const MAX_TRIES = 12; // ~30s at 2.5s interval

export default function PaymentResult({ kind }) {
  const { refresh } = usePlayer();
  const [state, setState] = useState(kind === "cancel" ? "cancel" : "checking"); // checking|paid|failed|cancel|timeout
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (kind === "cancel") return;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (!sid) { setState("failed"); return; }
    let cancelled = false;
    let n = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.paymentStatus(sid);
        if (data.payment_status === "paid") {
          setState("paid");
          try { Sound.init(); Sound.evolution(); } catch {}
          refresh && refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") { setState("failed"); return; }
      } catch (e) {
        console.warn("payment status poll failed", e);
        if (e?.response?.status === 404 && n >= 2) { setState("failed"); return; }
      }
      n += 1; setTries(n);
      if (n >= MAX_TRIES) { setState("timeout"); return; }
      setTimeout(poll, 2500);
    };
    poll();
    return () => { cancelled = true; };
  }, [kind, refresh]);

  const goHome = () => { window.location.href = "/"; };

  const Wrap = ({ children }) => (
    <div className="arena-bg grain fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="payment-result">
      <div className="card w-full max-w-md p-8 text-center pop-in">{children}</div>
    </div>
  );

  if (state === "checking") return (
    <Wrap>
      <Loader2 size={56} className="text-cyan-400 animate-spin mx-auto" />
      <div className="font-head font-black text-2xl mt-4 text-white">Confirming Payment...</div>
      <div className="text-slate-400 text-sm mt-1">Securely verifying with Stripe{".".repeat((tries % 3) + 1)}</div>
    </Wrap>
  );
  if (state === "paid") return (
    <Wrap>
      <CheckCircle2 size={64} className="text-green-400 mx-auto" data-testid="payment-success" />
      <div className="font-head font-black text-3xl mt-4 text-white">Purchase Complete!</div>
      <div className="text-slate-300 mt-2 flex items-center justify-center gap-2">
        <Coins size={18} className="text-amber-400" /> Your rewards have been added to your account.
      </div>
      <button onClick={goHome} className="btn-primary w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="payment-continue"><Home size={18} /> Back to Menu</button>
    </Wrap>
  );
  if (state === "cancel") return (
    <Wrap>
      <XCircle size={56} className="text-slate-400 mx-auto" data-testid="payment-cancel" />
      <div className="font-head font-black text-2xl mt-4 text-white">Checkout Cancelled</div>
      <div className="text-slate-400 text-sm mt-1">No charge was made. You can try again anytime.</div>
      <button onClick={goHome} className="btn-primary w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="payment-continue"><Home size={18} /> Back to Menu</button>
    </Wrap>
  );
  return (
    <Wrap>
      <ShieldOff size={56} className="text-red-400 mx-auto" />
      <div className="font-head font-black text-2xl mt-4 text-white">{state === "timeout" ? "Still Processing" : "Payment Issue"}</div>
      <div className="text-slate-400 text-sm mt-1">{state === "timeout" ? "Your payment is taking longer than expected. If you were charged, your rewards will appear shortly." : "We couldn't confirm this payment. If you were charged, contact support."}</div>
      <button onClick={goHome} className="btn-primary w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="payment-continue"><Home size={18} /> Back to Menu</button>
    </Wrap>
  );
}
